import { Link } from "react-router-dom";
import { RotateCcw, Clock, X } from "lucide-react";
import { getLastCase, clearLastCase } from "../lib/chairsideStore";
import { emit, EVENTS } from "../lib/analytics";
import { useState } from "react";

/**
 * ResumeLastCaseCard — chairside affordance shown at the top of Ask the
 * Bench (mobile home). One tap re-opens the last case the clinician
 * touched. Card is dismissable per-session.
 *
 * NO PHI on the card itself. Shows the opaque case ID (last 6 chars),
 * a relative time-ago label, and the workflow status hint if we stored one.
 */
function _timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch (_) { return ""; }
}

export default function ResumeLastCaseCard() {
  const [dismissed, setDismissed] = useState(false);
  const last = getLastCase();
  if (!last || dismissed) return null;

  const label = last.id.slice(-6).toUpperCase();
  const ago = _timeAgo(last.at);

  return (
    <div
      data-testid="resume-last-case-card"
      className="mt-4 p-4 rounded-lg border border-[var(--pa-gold)]/50 bg-[var(--pa-gold)]/10 flex items-center gap-3"
    >
      <div className="shrink-0 h-10 w-10 rounded-full bg-[var(--pa-gold)]/20 flex items-center justify-center">
        <RotateCcw size={18} className="text-[var(--pa-gold)]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[var(--pa-gold)]">Resume last case?</div>
        <div className="mt-0.5 text-sm text-[var(--pa-text)] flex items-center gap-2 flex-wrap">
          <span data-testid="resume-last-case-id">Case #{label}</span>
          {ago && (
            <span className="text-[11px] text-[var(--pa-text-dim)] inline-flex items-center gap-1">
              <Clock size={11} /> {ago}
            </span>
          )}
          {last.status && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--pa-surface)] text-[var(--pa-text-muted)] uppercase">
              {last.status}
            </span>
          )}
        </div>
      </div>
      <Link
        to={`/quick-assessment/${last.id}`}
        data-testid="resume-last-case-cta"
        onClick={() => emit(EVENTS.RESUME_LAST_CASE, { feature: "resume_last_case", input_mode: "tap" })}
        className="shrink-0 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-3 py-2 rounded-md text-sm hover:bg-[var(--pa-gold-hover)] transition"
      >
        Reopen
      </Link>
      <button
        onClick={() => { clearLastCase(); setDismissed(true); }}
        data-testid="resume-last-case-dismiss"
        aria-label="Dismiss"
        className="shrink-0 text-[var(--pa-text-dim)] hover:text-[var(--pa-text)] p-1"
      >
        <X size={14} />
      </button>
    </div>
  );
}
